import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextareaInput, NumberInput, DatePicker, CurrencyInput } from 'components/FormControl';
import { defaultValues } from 'features/ProjectProposal/defaultValues';
import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { getResearchKpiGroup, formatFileNameProduct } from 'utils/func';

import TransferTechnologyDialog from './TransferTechnologyDialog';

export default function TransferTechnologyTable({
  researchKpis,
  researchKpiGroups,
  disabled,
  control,
  errors,
  projectCode,
  handleDownloadFile,
  setTransferredTechnologyFilesDelete,
  yearProposed,
}) {
  // #region Data
  const { t } = useTranslation();
  const trainingResultRef = useRef();

  const { fields, insert, remove, update } = useFieldArray({
    control,
    name: 'researchResult.transferTechnology',
    keyName: 'customId',
  });
  const researchKpiGroupOptions = getResearchKpiGroup(researchKpis);

  // #endregion Data
  return (
    <>
      <h4>{t('r05.b.b2.technologyOrSolution.title')}</h4>

      <div className="form-table p-fluid">
        <table className="mb-2">
          <thead>
            <tr>
              <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                {t('formLayout.numericalOrder')}
              </th>
              <th className="text-center" style={{ minWidth: 170, width: 170 }}>
                {t('r05.b.b2.technologyOrSolution.name')}
              </th>
              <th className="text-center" style={{ minWidth: 70, width: 70 }}>
                {t('r05.b.b2.technologyOrSolution.year')}
              </th>
              <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                {t('r05.b.b2.technologyOrSolution.partner')}
              </th>
              <th className="text-center" style={{ minWidth: 150, width: 150 }}>
                {t('r05.b.b2.technologyOrSolution.date')}
              </th>
              <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                {t('r05.b.b2.technologyOrSolution.revenue')}
              </th>
              <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                {t('r05.b.b2.technologyOrSolution.scale')}
              </th>
              <th className="text-center" style={{ minWidth: 140, width: 140 }}>
                {t('r05.b.b2.table.proof')}
              </th>
              {!disabled && (
                <th rowSpan={3} className="float text-center" style={{ minWidth: 60, width: 60 }}>
                  {t('projectProposal.action')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {fields?.map((item, index) => (
              <tr key={item.customId}>
                <td className="text-center">{index + 1}</td>
                <td>
                  <TextareaInput
                    name={`researchResult.transferTechnology.${index}.name`}
                    control={control}
                    errors={errors}
                    readOnly
                  />
                </td>
                <td>
                  <NumberInput
                    name={`researchResult.transferTechnology.${index}.transferredYear`}
                    control={control}
                    errors={errors}
                    options={researchKpiGroupOptions}
                    readOnly
                  />
                </td>
                <td>
                  <TextareaInput
                    name={`researchResult.transferTechnology.${index}.partner`}
                    isRequired
                    control={control}
                    errors={errors}
                    options={researchKpis}
                    readOnly
                  />
                </td>
                <td>
                  <DatePicker
                    name={`researchResult.transferTechnology.${index}.contractedDate`}
                    control={control}
                    errors={errors}
                    readOnly
                  />
                </td>
                <td>
                  <CurrencyInput
                    name={`researchResult.transferTechnology.${index}.revenue`}
                    control={control}
                    errors={errors}
                    readOnly
                  />
                </td>
                <td>
                  <TextareaInput
                    name={`researchResult.transferTechnology.${index}.scale`}
                    control={control}
                    errors={errors}
                    readOnly
                  />
                </td>
                <td>
                  <input
                    type="file"
                    name={`researchResult.transferTechnology.${index}.file`}
                    disabled
                    hidden
                  />
                  {!item?.file?.name ? (
                    item?.fileUrl && (
                      <span
                        className="p-button p-button-text p-button-sm mt-1 p-0"
                        onClick={
                          () =>
                            handleDownloadFile(
                              item?.fileUrl,
                              formatFileNameProduct(
                                item?.fileUrl || '',
                                projectCode,
                                'CGCN',
                                index + 1
                              )
                            )
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      >
                        <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                        <span className="text-blue-500 underline font-italic ml-1">
                          {formatFileNameProduct(
                            item?.fileUrl || '',
                            projectCode,
                            'CGCN',
                            index + 1
                          )}
                        </span>
                      </span>
                    )
                  ) : (
                    <span>
                      {formatFileNameProduct(
                        item?.file?.name || '',
                        projectCode,
                        'CGCN',
                        index + 1
                      )}
                    </span>
                  )}
                </td>

                {!disabled && (
                  <td className="float text-center p-0">
                    <div className="">
                      <i
                        onClick={() => {
                          trainingResultRef.current.open({
                            projectCode,
                            index,
                            value: fields[index],
                            update,
                            type: 'UPDATE',
                          });
                        }}
                        className="edit-icon pi pi-pencil text-primary cursor-pointer mt-1"
                        data-pr-tooltip={t('formLayout.action.edit')}
                        data-pr-position="left"
                      />
                      <Tooltip target=".edit-icon" />
                    </div>

                    <i
                      onClick={() => {
                        if (item?.id) {
                          setTransferredTechnologyFilesDelete((prev) => [...prev, item.id]);
                        }
                        remove(index);
                      }}
                      className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
                      data-pr-tooltip={t('formLayout.action.remove')}
                      data-pr-position="left"
                    />
                    <Tooltip target=".remove-icon" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!disabled && (
        <div className="flex justify-content-center mt-2">
          <i
            onClick={() => {
              trainingResultRef.current.open({
                projectCode,
                index: fields?.length ?? 0,
                value: defaultValues.hardProduct,
                insert,
                type: 'INSERT',
              });
            }}
            className="add-hard-product pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
            data-pr-tooltip={t('formLayout.action.add')}
            data-pr-position="left"
          />
          <Tooltip target=".add-hard-product" />
        </div>
      )}

      <TransferTechnologyDialog
        researchKpis={researchKpis}
        researchKpiGroups={researchKpiGroups}
        ref={trainingResultRef}
        handleDownloadFile={handleDownloadFile}
        yearProposed={yearProposed || 2025}
      />
    </>
  );
}

TransferTechnologyTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  researchKpis: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  researchKpiGroups: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  projectCode: PropTypes.string.isRequired,
  handleDownloadFile: PropTypes.func,
  setTransferredTechnologyFilesDelete: PropTypes.func,
  yearProposed: PropTypes.number,
};

TransferTechnologyTable.defaultProps = {
  disabled: false,
  handleDownloadFile: () => null,
  setTransferredTechnologyFilesDelete: () => null,
  yearProposed: null,
};
